// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { MoreHorizontal, Pencil, Power, PowerOff, Store, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { cn } from "@/lib/utils";

interface UserActionsMenuProps {
  isActive: boolean;
  isAdmin: boolean;
  isSelf: boolean;
  onChangeStore: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  /** Adds Edit at the top of the menu. The list rows pass it; the detail header has its own Edit button. */
  onEdit?: () => void;
  triggerClassName?: string;
}

export function UserActionsMenu({
  isActive,
  isAdmin,
  isSelf,
  onChangeStore,
  onToggleActive,
  onDelete,
  onEdit,
  triggerClassName,
}: UserActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamUsersEdit);
  if (!canEdit) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        {onEdit && (
          <>
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="h-4 w-4" />
              {t("buttons.edit")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onSelect={onChangeStore}>
          <Store className="h-4 w-4" />
          {t("users.list.actions.changeStore")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isSelf ? (
          // You're always active while signed in, so this is the Deactivate variant — blocked for self.
          // Kept visible-but-disabled (with a reason) rather than hidden, so the restriction is discoverable.
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                aria-disabled
                className="text-destructive opacity-50 cursor-not-allowed focus:text-destructive"
              >
                <PowerOff className="h-4 w-4 text-destructive" />
                {t("users.list.actions.deactivate")}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent>{t("users.list.actions.cannotModifySelf")}</TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuItem
            onSelect={onToggleActive}
            className={isActive ? "text-destructive focus:text-destructive" : ""}
          >
            {isActive ? (
              <>
                <PowerOff className="h-4 w-4 text-destructive" />
                {t("users.list.actions.deactivate")}
              </>
            ) : (
              <>
                <Power className="h-4 w-4" />
                {t("users.list.actions.activate")}
              </>
            )}
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            {isSelf ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    aria-disabled
                    variant="destructive"
                    className="opacity-50 cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("users.list.actions.delete")}
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent>{t("users.list.actions.cannotModifySelf")}</TooltipContent>
              </Tooltip>
            ) : (
              <DropdownMenuItem onSelect={onDelete} variant="destructive">
                <Trash2 className="h-4 w-4" />
                {t("users.list.actions.delete")}
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
